import { Button, Textarea, TextInput } from '@mantine/core';
import { type KeyboardEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './InlineTextEditor.module.css';

export interface InlineTextEditorProps {
  value: string;
  /** Reject to stay in the editor; pass the reason as `error`, or let the API layer report it. */
  onSave: (next: string) => Promise<void>;
  onCancel: () => void;
  mono?: boolean;
  placeholder?: string;
  help?: ReactNode;
  /** A textarea; Ctrl or Cmd+Enter saves, Enter adds a line. */
  multiline?: boolean;
  /** Accessible name of the input. */
  label?: string;
  /** Why the last save was refused, shown under the input. */
  error?: ReactNode;
}

export function InlineTextEditor({
  value,
  onSave,
  onCancel,
  mono,
  placeholder,
  help,
  multiline,
  label,
  error,
}: InlineTextEditorProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const errorId = useId();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await onSave(draft);
    } catch {
      // stay in the editor with the draft intact
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    } else if (e.key === 'Enter' && (!multiline || e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void save();
    }
  };

  const inputProps = {
    value: draft,
    onChange: (e: { currentTarget: { value: string } }) => setDraft(e.currentTarget.value),
    onKeyDown,
    placeholder,
    'aria-label': label,
    ref: inputRef,
    readOnly: saving,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': error ? errorId : undefined,
    className: `${classes.input}${mono ? ` ${classes.mono}` : ''}`,
  };

  return (
    <div className={classes.editor}>
      <div className={classes.line}>
        {multiline ? (
          <Textarea {...inputProps} autosize minRows={3} maxRows={12} />
        ) : (
          <TextInput {...inputProps} />
        )}
        <div className={classes.buttons}>
          <Button variant="filled" size="xs" onClick={() => void save()} loading={saving}>
            {t('ui.save')}
          </Button>
          <Button size="xs" onClick={onCancel} disabled={saving}>
            {t('ui.cancel')}
          </Button>
        </div>
      </div>
      {error && (
        <span id={errorId} className={classes.error} role="alert">
          {error}
        </span>
      )}
      {help && <span className={classes.help}>{help}</span>}
    </div>
  );
}
